/**
 * Created by Jerker Nord on 2022-04-27.
 */
import {TheBorderSymbol} from "./TheBorderSymbol";
import {gsap} from "gsap";
import {TheBorder} from "../TheBorder";
import {TheBorderGameAssets} from "../TheBorderGameAssets";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {TheBorderStatics} from "../TheBorderStatics";
import {TheBorderSound} from "../sound/TheBorderSound";

export class TheBorderXBombSymbol extends TheBorderSymbol {

    public static LANDING_ANIMATION: string = "landing";
    public static LANDING_ANIMATION_SMALL: string = TheBorderXBombSymbol.LANDING_ANIMATION + "_half";
    public static IDLE_ANIMATION: string = "idle";
    public static IDLE_ANIMATION_SMALL: string = TheBorderXBombSymbol.IDLE_ANIMATION + "_half";
    public static EXPLOSION_ANIMATION: string = "blast";
    public static EXPLOSION_ANIMATION_SMALL: string = TheBorderXBombSymbol.EXPLOSION_ANIMATION + "_half";
    public static EXPLOSION_ANIMATION_WR: string = "blast_w";
    public static EXPLOSION_ANIMATION_SMALL_WR: string = "blast_half_w";

    private _landingAndIdleTL: gsap.core.Timeline;
    private _idleTL: gsap.core.Timeline;
    private _explosionTL: gsap.core.Timeline;
    private _turnOffSpineTL: gsap.core.Timeline;

    constructor() {
        super();
        this.symbolName = "X_BOMB";
        this._splitByXSplit = false;
        this.createAnimations();
    }

    private _splitByXSplit: boolean;

    set splitByXSplit(value: boolean) {
        this._splitByXSplit = value;
    }

    public getIdleAnimation(): void {
        if (this._landingAndIdleTL) {
            this._landingAndIdleTL.pause();
            this._landingAndIdleTL.kill();
        }
        this._idleTL = gsap.timeline();
        this._idleTL.add(() => {
            let animName: string = TheBorderXBombSymbol.IDLE_ANIMATION,
                yOffset: number = 0;

            if (this.sizeMultiplier == 0.5) {
                animName = TheBorderXBombSymbol.IDLE_ANIMATION_SMALL;
                yOffset = -25;
            }

            this._spineAnimation.y = yOffset;
            this.resetSpine();
            new SpineTween(this._spineAnimation, 0, animName, -1);
        });
    }

    public getLandingAndIdleAnimation(): gsap.core.Timeline {
        this.setProps();

        let landingAnimName: string = TheBorderXBombSymbol.LANDING_ANIMATION,
            idleAnimName: string = TheBorderXBombSymbol.IDLE_ANIMATION,
            yOffset: number = 0;

        if (this.sizeMultiplier === 0.5) {
            landingAnimName = TheBorderXBombSymbol.LANDING_ANIMATION_SMALL;
            idleAnimName = TheBorderXBombSymbol.IDLE_ANIMATION_SMALL;
            yOffset = -25;
        }

        this._landingAndIdleTL = gsap.timeline({paused: true});
        this._landingAndIdleTL.add(() => {
            this._spineAnimation.y = yOffset;
            this.resetSpine();
        });
        this._landingAndIdleTL.add(new SpineTween(this._spineAnimation, 0, landingAnimName));
        this._landingAndIdleTL.add(() => {
            this._spineAnimation.y = yOffset;
            this.resetSpine();
            new SpineTween(this._spineAnimation, 0, idleAnimName, -1);
        });

        return this._landingAndIdleTL;
    }

    public playXBombExplosionAnimation(bombIndex: number): gsap.core.Timeline {
        if (this._landingAndIdleTL) {
            this._landingAndIdleTL.pause();
            this._landingAndIdleTL.kill();
        }
        if (this._idleTL) {
            this._idleTL.pause();
            this._idleTL.kill();
        }

        let explodeAnimName: string = TheBorderXBombSymbol.EXPLOSION_ANIMATION,
            yOffset: number = 0;

        if (this.sizeMultiplier === 0.5) {
            explodeAnimName = TheBorderXBombSymbol.EXPLOSION_ANIMATION_SMALL;
            yOffset = -25;
        }

        if (this.aliasName === "WR") {
            explodeAnimName = TheBorderXBombSymbol.EXPLOSION_ANIMATION_WR;
            if (this.sizeMultiplier === 0.5) {
                explodeAnimName = TheBorderXBombSymbol.EXPLOSION_ANIMATION_SMALL_WR;
            }
        }

        this._explosionTL = gsap.timeline({paused: true});
        this._explosionTL.add(() => {
            this.setExplosionProps();
            this._spineAnimation.y = yOffset;
            this.resetSpine();
            bombIndex === 0 && TheBorder.soundPlayer.playSound(TheBorderSound.BOMB_EXPLODE); //#50, #306
        });
        this._explosionTL.add(new SpineTween(this._spineAnimation, 0, explodeAnimName));

        return this._explosionTL;
    }

    public turnOffSymbolSpine(): void {
        if (this._landingAndIdleTL) {
            this._landingAndIdleTL.pause();
            this._landingAndIdleTL.kill();
        }
        if (this._idleTL) {
            this._idleTL.pause();
            this._idleTL.kill();
        }
        if (this._explosionTL) {
            this._explosionTL.pause();
            this._explosionTL.kill();
        }

        this._turnOffSpineTL.restart();

        this.addChild(this.splitLevelContainer);
    }

    public reset(): void {
        this.symbolName = "X_BOMB";
        this._splitByXSplit = false;
        super.reset();
    }

    public setExplosionProps(): void {
        this.topLayerContainer.position.x = (TheBorderStatics.symbolWidth * this.reelId);
        this.topLayerContainer.position.y = this.y;

        this.topLayerContainer.addChild(this.splitLevelContainer, this.darkOverlayContainer);
        TheBorder.symbolsOnTopView.addChild(this.topLayerContainer);
    }

    public setProps(): void {
        this.topLayerContainer.position.x = (TheBorderStatics.symbolWidth * this.reelId);
        this.topLayerContainer.position.y = this.y;

        this.topLayerContainer.addChild(this.splitLevelContainer, this.darkOverlayContainer);
        TheBorder.allReelsView.addChild(this.topLayerContainer);
    }

    private createAnimations(): void {
        this._spineAnimation = new PIXI.spine.Spine(TheBorder.assetLoader.getSpineAsset(TheBorderGameAssets.X_BOMB_SPINE));
        this._spineAnimation.visible = false;
        this._spineContainer.addChild(this._spineAnimation);
        this.topLayerContainer.addChild(this._spineContainer);
        this.topLayerContainer.addChild(this.darkOverlayContainer);

        this._turnOffSpineTL = gsap.timeline({paused: true});
        this._turnOffSpineTL.add(gsap.to(this._spineAnimation, {duration: 0.2, alpha: 0}));
        this._turnOffSpineTL.add(() => {
            this._spineAnimation.visible = false;
            this._spineAnimation.state.clearTracks();
            this._spineAnimation.alpha = 1;
        });
    }

    private resetSpine(): void {
        if (!this._spineAnimation.visible) {
            this._spineAnimation.visible = true;
            this._spineAnimation.updateTransform();
        }
        this._spineAnimation.state.clearTracks();
        this._spineAnimation.skeleton.setSlotsToSetupPose();
        this._spineAnimation.skeleton.setToSetupPose();
        this._spineAnimation.state.setEmptyAnimation(0, 0);
    }
}